export interface ITransitionProps {
  currentPathname: string;
  nextPathname: string;
}

export interface ViewTransitionCallbacks {
  onBeforeTransition?: (props: ITransitionProps) => Promise<void> | void;
  onAfterTransition?: () => Promise<void> | void;
}

export const viewTransitionCallbacksMap = new Map<string, ViewTransitionCallbacks>();

/**
 * Register the view transition callbacks.
 */
export const registerViewTransitionCallbacks = (
  name: string,
  callbacks: ViewTransitionCallbacks
) => {
  viewTransitionCallbacksMap.set(name, callbacks);
};

export const unregisterViewTransitionCallbacks = (name: string) => {
  viewTransitionCallbacksMap.delete(name);
};

/**
 * Runs the before transition callbacks. Encapsulates the error handling for individual callbacks.
 */
export const runBeforeTransitionCallbacks = async (props: ITransitionProps): Promise<void> => {
  // Runs all the before transition callbacks in parallel.
  await Promise.allSettled(
    Array.from(viewTransitionCallbacksMap.entries()).map(
      ([name, transition]: [string, ViewTransitionCallbacks]): Promise<void> => {
        const beforeTransitionPromise = Promise.resolve(transition.onBeforeTransition?.(props));
        // Display the error in the console, but don't block the execution of the next callback.
        beforeTransitionPromise.catch((error) => {
          console.error(`onBeforeTransition '${name}' failed or was skipped:`, error);
        });
        return beforeTransitionPromise;
      }
    )
  );
};

/**
 * Runs the after transition callbacks. Encapsulates the error handling for individual callbacks.
 */
export const runAfterTransitionCallbacks = async (): Promise<void> => {
  // Run all the after transition callbacks in parallel.
  await Promise.allSettled(
    Array.from(viewTransitionCallbacksMap.entries()).map(
      ([name, transition]: [string, ViewTransitionCallbacks]): Promise<void> => {
        const afterTransitionPromise = Promise.resolve(transition.onAfterTransition?.());
        // Display the error in the console, but don't block the execution of the next callback.
        afterTransitionPromise.catch((error) => {
          console.error(`onAfterTransition '${name}' failed or was skipped:`, error);
        });
        return afterTransitionPromise;
      }
    )
  );
};
